// Date presets for the Partner Insights date filter (Metabase's lead_date filter).
// Ranges are inclusive, in the viewer's local calendar, as YYYY-MM-DD.
// "Last N days" ends yesterday, as Metabase's "Previous N days" does: today's
// leads reach Redshift a day late, so counting today would undercount the range.

export type DatePreset =
	| "today"
	| "last_7_days"
	| "last_30_days"
	| "last_90_days"
	| "this_month"
	| "last_month"
	| "last_quarter"
	| "year_to_date"
	| "all"
	| "custom";

export const PRESET_LABELS: Record<DatePreset, string> = {
	today: "Today",
	last_7_days: "Last 7 days",
	last_30_days: "Last 30 days",
	last_90_days: "Last 90 days",
	this_month: "This month",
	last_month: "Last month",
	last_quarter: "Last quarter",
	year_to_date: "Year to date",
	all: "All time",
	custom: "Custom range…",
};

export const DATE_PRESETS = Object.keys(PRESET_LABELS) as DatePreset[];

/** The presets each surface lists, in the order the design lists them. */
export const POPOVER_PRESETS: DatePreset[] = [
	"today",
	"last_7_days",
	"last_30_days",
	"last_90_days",
	"this_month",
	"last_month",
	"year_to_date",
];
export const DIALOG_PRESETS: DatePreset[] = [
	"today",
	"last_7_days",
	"last_30_days",
	"this_month",
	"last_month",
	"last_quarter",
];
export const SHEET_PRESETS: DatePreset[] = [
	"today",
	"last_7_days",
	"last_30_days",
	"last_90_days",
	"this_month",
];

export const DEFAULT_PRESET: DatePreset = "last_30_days";

export function toIsoDate(date: Date): string {
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function parseIsoDate(value: string): Date {
	const [year, month, day] = value.split("-").map(Number);
	return new Date(year, month - 1, day);
}

export function isIsoDate(value: unknown): value is string {
	return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function addDays(date: Date, days: number): Date {
	const next = new Date(date);
	next.setDate(next.getDate() + days);
	return next;
}

/** The `days` days before today (today left out). */
function lastDays(today: Date, days: number): { from: string; to: string } {
	return {
		from: toIsoDate(addDays(today, -days)),
		to: toIsoDate(addDays(today, -1)),
	};
}

/** The range a preset stands for, or null for "all" and "custom". */
export function presetRange(
	preset: DatePreset,
	today: Date = new Date(),
): { from: string; to: string } | null {
	const year = today.getFullYear();
	const month = today.getMonth();
	switch (preset) {
		case "today":
			return { from: toIsoDate(today), to: toIsoDate(today) };
		case "last_7_days":
			return lastDays(today, 7);
		case "last_30_days":
			return lastDays(today, 30);
		case "last_90_days":
			return lastDays(today, 90);
		case "this_month":
			return {
				from: toIsoDate(new Date(year, month, 1)),
				to: toIsoDate(today),
			};
		case "last_month":
			return {
				from: toIsoDate(new Date(year, month - 1, 1)),
				to: toIsoDate(new Date(year, month, 0)),
			};
		case "last_quarter": {
			const quarterStart = Math.floor(month / 3) * 3;
			return {
				from: toIsoDate(new Date(year, quarterStart - 3, 1)),
				to: toIsoDate(new Date(year, quarterStart, 0)),
			};
		}
		case "year_to_date":
			return { from: `${year}-01-01`, to: toIsoDate(today) };
		default:
			return null;
	}
}

/** Days in an inclusive range. */
export function rangeLength(from: string, to: string): number {
	const ms = parseIsoDate(to).getTime() - parseIsoDate(from).getTime();
	return Math.round(ms / 86_400_000) + 1;
}

/** The same number of days just before the range: what "previous period" compares to. */
export function previousRange(
	from: string,
	to: string,
): { from: string; to: string } {
	const days = rangeLength(from, to);
	const previousTo = addDays(parseIsoDate(from), -1);
	return {
		from: toIsoDate(addDays(previousTo, -(days - 1))),
		to: toIsoDate(previousTo),
	};
}

// Three-letter months as the design writes them ("Sep", where en-GB says "Sept").
export const MONTHS_SHORT = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

/** "12 Aug" */
export function shortDate(date: Date): string {
	return `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]}`;
}

/** "12 Aug 2026" */
export function longDate(date: Date): string {
	return `${shortDate(date)} ${date.getFullYear()}`;
}

/** "12 Aug 2026" */
export function formatLongDate(value: string): string {
	return longDate(parseIsoDate(value));
}

/** "12 Aug – 10 Sep", with years only when the two differ. */
export function formatRange(from: string, to: string): string {
	const start = parseIsoDate(from);
	const end = parseIsoDate(to);
	const format =
		start.getFullYear() === end.getFullYear() ? shortDate : longDate;
	return from === to ? format(start) : `${format(start)} – ${format(end)}`;
}

/** Chip text for the date filter: the preset's name, or the custom range. */
export function dateChipLabel(
	preset: DatePreset,
	from: string | null,
	to: string | null,
): string {
	if (preset === "custom" && from && to) return formatRange(from, to);
	return PRESET_LABELS[preset];
}

/** "last 30 days", for subtitles such as "Destination cities in UK · last 30 days". */
export function dateSubtitle(
	preset: DatePreset,
	from: string | null,
	to: string | null,
): string {
	if (preset === "custom")
		return from && to ? formatRange(from, to) : "all time";
	return PRESET_LABELS[preset].toLowerCase();
}

/** "previous 30 days" */
export function comparisonLabel(from: string, to: string): string {
	const days = rangeLength(from, to);
	return days === 1 ? "previous day" : `previous ${days} days`;
}
